import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE, type PersistedState } from 'redux-persist';
import ApplicationReducer from './ApplicationSlice';
import ControllerDataReducer from './ControllerDataSlice';
import EditorReducer from './EditorSlice';
import ProjectReducer from './ProjectSlice';
import RobotDataReducer from './RobotDataSlice';
import RobotReducer from './RobotSlice';
import { analyticsMiddleware } from './analyticsMiddleware';
import { runSafePersistedProjectMigration } from '../utils/projectMigrations';
import { PROJECT_PERSIST_VERSION, projectPersistIdbStorage } from '../utils/persistProjectStorage';

const storage = projectPersistIdbStorage;

const persistProjectConfig = {
  key: 'PROJECT',
  storage,
  version: PROJECT_PERSIST_VERSION,
  migrate: async (state: unknown, version: number): Promise<PersistedState> =>
    runSafePersistedProjectMigration(state, version, storage, PROJECT_PERSIST_VERSION),
  whitelist: ['projects', 'loadedProject', 'leftDrawerWidths', 'bottomDrawerHeight'],
};

const persistRobotDataConfig = {
  key: 'ROBOT_DATA',
  storage,
  whitelist: ['userRobotColor', 'sensorData', 'slotStorage'],
};

const persistApplicationConfig = {
  key: 'APPLICATION',
  storage,
  whitelist: ['cookiesBannerOpen', 'autoSwapToDashboard'],
};

const rootReducer = combineReducers({
  application: persistReducer(persistApplicationConfig, ApplicationReducer),
  robot: RobotReducer,
  robotData: persistReducer(persistRobotDataConfig, RobotDataReducer),
  controllerData: ControllerDataReducer,
  project: persistReducer(persistProjectConfig, ProjectReducer),
  editor: EditorReducer,
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(analyticsMiddleware),
});

export type AppState = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);

export default store;
